
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type OverlayShaderUniforms = {
	uViewProjection: WebGLUniformLocation,
	uInvDisplaySize: WebGLUniformLocation,
	uSceneDepth: WebGLUniformLocation,
	uMsdf: WebGLUniformLocation,
	uDepthTested: WebGLUniformLocation,
	uHasMsdf: WebGLUniformLocation,
	uPxRange: WebGLUniformLocation,
	uAtlasSize: WebGLUniformLocation
};

export type OverlayShaderAttributes = {
	aQuadCorner: number,
	iWorldPos: number,
	iSizePx: number,
	iScreenOffsetPx: number,
	iColor: number,
	iUvOrigin: number,
	iUvSize: number,
	vColor: number,
	vScreenUv: number,
	vUv: number
};

export type OverlayShaderSettings = string[];

export class OverlayShader extends ShaderBase<OverlayShaderUniforms, OverlayShaderAttributes> {

    private static fragmentShaderSource = `
// Procedural colored quads or MSDF glyphs, switched at draw time by the
// uHasMsdf flag (set per batch in OverlayPass).
//
// MSDF formula follows the standard from Chlumsky/msdfgen: take the median
// of the three SDF channels, convert that to a screen-space pixel distance
// via the atlas-to-screen scale derived from fwidth(vUv), then smoothstep
// across the 0.5 isovalue for the glyph edge.

in vec4 vColor;
in vec2 vScreenUv;
in vec2 vUv;

out vec4 fragColor;

uniform sampler2D uSceneDepth;
uniform sampler2D uMsdf;
uniform bool uDepthTested;
uniform bool uHasMsdf;
uniform float uPxRange;                // distanceRange (atlas px) from the BMFont file
uniform vec2 uAtlasSize;               // atlas (width, height) in pixels

float median3(vec3 v) {
    return max(min(v.r, v.g), min(max(v.r, v.g), v.b));
}

float screenPxRange() {
    // Convert the atlas-pixel distance range to a screen-pixel range. The
    // floor of 1.0 keeps glyphs legible when they shrink below the atlas's
    // native resolution.
    vec2 unitRange = vec2(uPxRange) / uAtlasSize;
    vec2 screenTexSize = vec2(1.0) / fwidth(vUv);
    return max(0.5 * dot(unitRange, screenTexSize), 1.0);
}

void main() {
    if (uDepthTested) {
        // Reversed-Z: depth bigger = closer. Scene closer than overlay →
        // overlay is occluded → discard.
        float sceneDepth = texture(uSceneDepth, vScreenUv).r;
        if (sceneDepth > gl_FragCoord.z) discard;
    }

    if (uHasMsdf) {
        vec3 msd = texture(uMsdf, vUv).rgb;
        float sd = median3(msd) - 0.5;
        float screenSd = screenPxRange() * sd;
        float opacity = clamp(screenSd + 0.5, 0.0, 1.0);
        if (opacity <= 0.0) discard;
        fragColor = vec4(vColor.rgb, vColor.a * opacity);
    } else {
        fragColor = vColor;
    }
}
`;

    private static vertexShaderSource = `
// In-game overlay quads: viewer-facing, anchored at a world position, sized in
// display pixels with an additional pixel-space screen offset. Runs after all
// post-processing (TAA / bloom / FXAA) so the unjittered view-projection is
// used — there is no temporal accumulation to feed.
//
// Procedural quads pass iUvSize = (0, 0); the shader keeps the inert UV path
// branchless. MSDF text quads pass per-glyph atlas UVs.

layout(location = 0) in vec2 aQuadCorner;          // per-vertex: corner in [-0.5, 0.5]
layout(location = 1) in vec3 iWorldPos;            // per-instance: anchor position
layout(location = 2) in vec2 iSizePx;              // per-instance: quad size in display pixels
layout(location = 3) in vec2 iScreenOffsetPx;      // per-instance: 2D offset in display pixels (+Y is down)
layout(location = 4) in vec4 iColor;               // per-instance: rgba (alpha multiplied into output)
layout(location = 5) in vec2 iUvOrigin;            // per-instance: atlas UV top-left for MSDF glyphs (else 0,0)
layout(location = 6) in vec2 iUvSize;              // per-instance: atlas UV size for MSDF glyphs (else 0,0)

uniform mat4 uViewProjection;                      // unjittered view-projection
uniform vec2 uInvDisplaySize;                      // 1 / displayWidth, 1 / displayHeight

out vec4 vColor;
out vec2 vScreenUv;
out vec2 vUv;

void main() {
    vec4 clip = uViewProjection * vec4(iWorldPos, 1.0);

    // Behind the camera (w <= 0): clip-space is meaningless, push the vertex
    // off-screen so the whole instance is discarded by clipping.
    if (clip.w <= 0.0) {
        gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
        vColor = vec4(0.0);
        vScreenUv = vec2(0.0);
        vUv = vec2(0.0);
        return;
    }

    vec3 anchorNdc = clip.xyz / clip.w;

    // Pixel-space → NDC. +Y in pixel-space is down; +Y in NDC is up — flip.
    vec2 ndcOffset = (iScreenOffsetPx + aQuadCorner * iSizePx) * 2.0 * uInvDisplaySize;
    ndcOffset.y = -ndcOffset.y;

    vec2 finalNdc = anchorNdc.xy + ndcOffset;

    // Re-multiply by clip.w so the implicit perspective divide reproduces
    // finalNdc.xy and keeps the anchor's depth (so depth-test against the
    // scene's depth texture is meaningful — we want the overlay's anchor
    // depth, not the quad-corner extrapolation).
    gl_Position = vec4(finalNdc * clip.w, anchorNdc.z * clip.w, clip.w);

    vColor = iColor;
    vScreenUv = finalNdc * 0.5 + 0.5;
    // aQuadCorner is in [-0.5, 0.5] → shift to [0, 1] for UV interpolation.
    vUv = iUvOrigin + (aQuadCorner + 0.5) * iUvSize;
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: OverlayShaderUniformsContainer;
    public attributesContainer: OverlayShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: OverlayShaderSettings) {
        super(ctx, OverlayShader.fragmentShaderSource, OverlayShader.vertexShaderSource, settings);
        this.uniformsContainer = new OverlayShaderUniformsContainer(ctx, this);
        this.attributesContainer = new OverlayShaderShaderAttributesContainer(ctx, this);
    }
}

class OverlayShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: OverlayShader) {
        super(ctx);
    }

    public setViewProjection(value: mat4 | number[]) : OverlayShaderUniformsContainer {
        this.setValue('uViewProjection', this.shader.uniforms.uViewProjection, value, 'mat4');
        return this;
    }

    public setInvDisplaySize(value: vec2 | number[]) : OverlayShaderUniformsContainer {
        this.setValue('uInvDisplaySize', this.shader.uniforms.uInvDisplaySize, value, 'vec2');
        return this;
    }

    public setSceneDepth(texture: Texture) : OverlayShaderUniformsContainer {
        this.setTextureValue('uSceneDepth', this.shader.uniforms.uSceneDepth, texture, 'sampler2D');
        return this;
    }

    public setMsdf(texture: Texture) : OverlayShaderUniformsContainer {
        this.setTextureValue('uMsdf', this.shader.uniforms.uMsdf, texture, 'sampler2D');
        return this;
    }

    public setDepthTested(value: boolean) : OverlayShaderUniformsContainer {
        this.setValue('uDepthTested', this.shader.uniforms.uDepthTested, value, 'bool');
        return this;
    }

    public setHasMsdf(value: boolean) : OverlayShaderUniformsContainer {
        this.setValue('uHasMsdf', this.shader.uniforms.uHasMsdf, value, 'bool');
        return this;
    }

    public setPxRange(value: number) : OverlayShaderUniformsContainer {
        this.setValue('uPxRange', this.shader.uniforms.uPxRange, value, 'float');
        return this;
    }

    public setAtlasSize(value: vec2 | number[]) : OverlayShaderUniformsContainer {
        this.setValue('uAtlasSize', this.shader.uniforms.uAtlasSize, value, 'vec2');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uSceneDepth', uniform: this.shader.uniforms.uSceneDepth, dataType: 'sampler2D' },
			{ name: 'uMsdf', uniform: this.shader.uniforms.uMsdf, dataType: 'sampler2D' }
        ];
    }
}

class OverlayShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: OverlayShader) {
        super(ctx);
    }

    public setQuadCorner(settings: { length: number, offset: number, itemSize: number }) : OverlayShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aQuadCorner, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setiWorldPos(settings: { length: number, offset: number, itemSize: number }) : OverlayShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.iWorldPos, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setiSizePx(settings: { length: number, offset: number, itemSize: number }) : OverlayShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.iSizePx, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setiScreenOffsetPx(settings: { length: number, offset: number, itemSize: number }) : OverlayShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.iScreenOffsetPx, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setiColor(settings: { length: number, offset: number, itemSize: number }) : OverlayShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.iColor, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setiUvOrigin(settings: { length: number, offset: number, itemSize: number }) : OverlayShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.iUvOrigin, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setiUvSize(settings: { length: number, offset: number, itemSize: number }) : OverlayShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.iUvSize, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvColor(settings: { length: number, offset: number, itemSize: number }) : OverlayShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vColor, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvScreenUv(settings: { length: number, offset: number, itemSize: number }) : OverlayShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vScreenUv, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvUv(settings: { length: number, offset: number, itemSize: number }) : OverlayShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vUv, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}